import React, { useState, useEffect } from 'react';
import { useProjectMerge } from '../../hooks/useProjectMerge';
import { Button } from '../ui/Button/Button';
import type { MergedAspects } from '../../types/merge';
import type { Criterion } from '../../types/evaluation';

interface AspectsMergeProps {
  projectIds: [string, string];
  onComplete: (data: MergedAspects) => void;
  onBack: () => void;
  initialData?: MergedAspects;
}

export const AspectsMerge: React.FC<AspectsMergeProps> = ({
  projectIds,
  onComplete,
  onBack,
  initialData
}) => {
  const { mergeAspects, isLoading, error } = useProjectMerge();
  const [mergeData, setMergeData] = useState<MergedAspects | null>(initialData || null);
  const [duplicateActions, setDuplicateActions] = useState<{[key: string]: 'merge' | 'keep_first' | 'keep_second' | 'keep_both'}>({});
  const [editedAspects, setEditedAspects] = useState<Criterion[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!initialData) {
      loadMergeData();
    } else {
      const actions: {[key: string]: 'merge' | 'keep_first' | 'keep_second' | 'keep_both'} = {};
      initialData.duplicates.forEach((dup, index) => {
        actions[index.toString()] = dup.action;
      });
      setDuplicateActions(actions);
      setEditedAspects([...initialData.merged]);
    }
  }, [projectIds, initialData]);

  const loadMergeData = async () => {
    const result = await mergeAspects(projectIds);
    if (result) {
      setMergeData(result);
      const actions: {[key: string]: 'merge' | 'keep_first' | 'keep_second' | 'keep_both'} = {};
      result.duplicates.forEach((dup, index) => {
        actions[index.toString()] = dup.action;
      });
      setDuplicateActions(actions);
      setEditedAspects([...result.merged]);
    }
  };

  const handleDuplicateAction = (duplicateIndex: number, action: 'merge' | 'keep_first' | 'keep_second' | 'keep_both') => {
    setDuplicateActions(prev => ({
      ...prev,
      [duplicateIndex.toString()]: action
    }));
    setIsEditing(true);
    recalculateMergedAspects(duplicateIndex, action);
  };

  const recalculateMergedAspects = (changedDuplicateIndex: number, newAction: string) => {
    if (!mergeData) return;

    const project1Criteria = mergeData.originalProjects[projectIds[0]];
    const project2Criteria = mergeData.originalProjects[projectIds[1]];
    
    const newMerged = [...project1Criteria];
    
    project2Criteria.forEach((criterion2, index2) => {
      let shouldAdd = true;
      const mergedCriterion = criterion2;
      
      mergeData.duplicates.forEach((duplicate, dupIndex) => {
        if (duplicate.indices[1] === index2) {
          const action = dupIndex === changedDuplicateIndex ? newAction : duplicateActions[dupIndex.toString()];
          const criterion1 = project1Criteria[duplicate.indices[0]];
          
          switch (action) {
            case 'merge':
              const mergedIndex = duplicate.indices[0];
              newMerged[mergedIndex] = {
                title: criterion1.title,
                description: [
                  ...(criterion1.description || []),
                  ...(criterion2.description || []).filter(desc => 
                    !(criterion1.description || []).some(d => d.toLowerCase().trim() === desc.toLowerCase().trim())
                  )
                ]
              };
              shouldAdd = false;
              break;
            case 'keep_first':
              shouldAdd = false;
              break;
            case 'keep_second':
              const targetIndex = duplicate.indices[0];
              newMerged[targetIndex] = criterion2;
              shouldAdd = false;
              break;
            case 'keep_both':
              break;
          }
        }
      });
      
      if (shouldAdd) {
        newMerged.push(mergedCriterion);
      }
    });
    
    setEditedAspects(newMerged);
  };

  const handleAspectEdit = (index: number, updatedAspect: Criterion) => {
    const newAspects = [...editedAspects];
    newAspects[index] = updatedAspect;
    setEditedAspects(newAspects);
    setIsEditing(true);
  };

  const handleAspectAdd = () => {
    const newAspect: Criterion = {
      title: '新しい観点',
      description: ['観点の説明を入力してください']
    };
    setEditedAspects([...editedAspects, newAspect]);
    setIsEditing(true);
  };

  const handleAspectRemove = (index: number) => {
    const newAspects = editedAspects.filter((_, i) => i !== index);
    setEditedAspects(newAspects);
    setIsEditing(true);
  };

  const handleComplete = () => {
    if (!mergeData) return;

    const updatedDuplicates = mergeData.duplicates.map((dup, index) => ({
      ...dup,
      action: duplicateActions[index.toString()] as 'merge' | 'keep_first' | 'keep_second' | 'keep_both' ?? dup.action
    }));

    const finalData: MergedAspects = {
      ...mergeData,
      duplicates: updatedDuplicates,
      merged: editedAspects,
      userEdited: isEditing
    };

    setMergeData(finalData);
    onComplete(finalData);
  };

  const getProjectName = (projectId: string): string => {
    const index = projectIds.indexOf(projectId);
    return index === 0 ? 'プロジェクト 1' : 'プロジェクト 2';
  };

  if (isLoading) {
    return (
      <div className="aspects-merge">
        <div className="loading">
          <div className="loading-spinner" />
          <p>観点を統合中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aspects-merge">
        <div className="error">
          <p>{error}</p>
          <Button onClick={loadMergeData}>再試行</Button>
        </div>
      </div>
    );
  }

  if (!mergeData) {
    return null;
  }

  const project1Criteria = mergeData.originalProjects[projectIds[0]];
  const project2Criteria = mergeData.originalProjects[projectIds[1]];

  return (
    <div className="aspects-merge">
      <div className="merge-header">
        <h2>観点の統合</h2>
        <p>システムが分析した観点統合案を確認し、手動で調整してください</p>
        <div className="instructions">
          <h4>💡 操作方法</h4>
          <ul>
            <li>類似観点の統合方法を選択してください（統合/保持/両方残す）</li>
            <li>統合後の観点リストを直接編集できます</li>
            <li>観点の追加・削除・説明文の修正が可能です</li>
            <li>最終確認後、次のステップに進んでください</li>
          </ul>
        </div>
      </div>

      <div className="merge-summary">
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">{getProjectName(projectIds[0])}</span>
            <span className="stat-value">{project1Criteria.length}件</span>
          </div>
          <span className="stat-plus">+</span>
          <div className="stat">
            <span className="stat-label">{getProjectName(projectIds[1])}</span>
            <span className="stat-value">{project2Criteria.length}件</span>
          </div>
          <span className="stat-arrow">→</span>
          <div className="stat">
            <span className="stat-label">統合後</span>
            <span className="stat-value">{editedAspects.length}件</span>
          </div>
        </div>
      </div>

      {mergeData.duplicates.length > 0 && (
        <div className="duplicates-section">
          <h3>🔍 類似観点の統合案（システム提案）</h3>
          <p>類似した観点が検出されました。統合方法を手動で選択してください。</p>
          
          <div className="duplicates-list">
            {mergeData.duplicates.map((duplicate, index) => {
              const criterion1 = project1Criteria[duplicate.indices[0]];
              const criterion2 = project2Criteria[duplicate.indices[1]];
              const currentAction = duplicateActions[index.toString()] ?? duplicate.action;
              
              return (
                <div key={index} className="duplicate-item">
                  <div className="duplicate-header">
                    <h4>類似観点 {index + 1}</h4>
                    <span className="similarity-score">
                      類似度: {(duplicate.similarity * 100).toFixed(0)}%
                    </span>
                  </div>
                  
                  <div className="criteria-comparison">
                    <div className="criterion-card">
                      <div className="criterion-source">{getProjectName(projectIds[0])}</div>
                      <h5>{criterion1.title}</h5>
                      <ul>
                        {(criterion1.description || []).map((desc, i) => (
                          <li key={i}>{desc}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="criterion-card">
                      <div className="criterion-source">{getProjectName(projectIds[1])}</div>
                      <h5>{criterion2.title}</h5>
                      <ul>
                        {(criterion2.description || []).map((desc, i) => (
                          <li key={i}>{desc}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="action-options">
                    <button
                      className={`action-option ${currentAction === 'merge' ? 'selected' : ''}`}
                      onClick={() => handleDuplicateAction(index, 'merge')}
                    >
                      <strong>統合</strong>
                      <span>タイトルは最初のものを使用し、説明を結合</span>
                    </button>
                    
                    <button
                      className={`action-option ${currentAction === 'keep_first' ? 'selected' : ''}`}
                      onClick={() => handleDuplicateAction(index, 'keep_first')}
                    >
                      <strong>{getProjectName(projectIds[0])}を使用</strong>
                      <span>最初のプロジェクトの観点のみを残す</span>
                    </button>
                    
                    <button
                      className={`action-option ${currentAction === 'keep_second' ? 'selected' : ''}`}
                      onClick={() => handleDuplicateAction(index, 'keep_second')}
                    >
                      <strong>{getProjectName(projectIds[1])}を使用</strong>
                      <span>2番目のプロジェクトの観点のみを残す</span>
                    </button>
                    
                    <button
                      className={`action-option ${currentAction === 'keep_both' ? 'selected' : ''}`}
                      onClick={() => handleDuplicateAction(index, 'keep_both')}
                    >
                      <strong>両方を保持</strong>
                      <span>別々の観点として両方を保持</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="aspects-editor">
        <div className="editor-header">
          <h3>✏️ 統合後の観点編集（手動調整）</h3>
          <Button variant="ghost" onClick={handleAspectAdd}>
            観点を追加
          </Button>
        </div>
        <p className="editor-description">
          統合された観点を確認し、必要に応じて編集してください。タイトルや説明文を直接変更できます。
        </p>
        
        <div className="aspects-list">
          {editedAspects.map((aspect, index) => (
            <div key={index} className="aspect-editor">
              <div className="aspect-header">
                <input
                  type="text"
                  value={aspect.title}
                  onChange={(e) => handleAspectEdit(index, { ...aspect, title: e.target.value })}
                  className="aspect-title-input"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAspectRemove(index)}
                  className="remove-button"
                >
                  削除
                </Button>
              </div>
              
              <div className="descriptions-editor">
                {(aspect.description || []).map((desc, descIndex) => (
                  <div key={descIndex} className="description-item">
                    <textarea
                      value={desc}
                      onChange={(e) => {
                        const newDescriptions = [...(aspect.description || [])];
                        newDescriptions[descIndex] = e.target.value;
                        handleAspectEdit(index, { ...aspect, description: newDescriptions });
                      }}
                      className="description-input"
                      rows={2}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newDescriptions = (aspect.description || []).filter((_, i) => i !== descIndex);
                        handleAspectEdit(index, { ...aspect, description: newDescriptions });
                      }}
                      className="remove-desc-button"
                    >
                      ×
                    </Button>
                  </div>
                ))}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newDescriptions = [...(aspect.description || []), '新しい説明'];
                    handleAspectEdit(index, { ...aspect, description: newDescriptions });
                  }}
                  className="add-desc-button"
                >
                  説明を追加
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="merge-actions">
        <Button variant="ghost" onClick={onBack}>
          戻る
        </Button>
        <Button variant="primary" onClick={handleComplete}>
          次のステップへ
        </Button>
      </div>

      <style>{`
        .aspects-merge {
          padding: 1rem;
        }

        .merge-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .merge-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .merge-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .instructions {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .instructions h4 {
          color: #0c4a6e;
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .instructions ul {
          color: #075985;
          margin: 0;
          padding-left: 1.5rem;
        }

        .instructions li {
          margin-bottom: 0.25rem;
        }

        .editor-description {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0 0 1rem 0;
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 0.375rem;
        }

        .loading, .error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .loading-spinner {
          width: 2rem;
          height: 2rem;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .merge-summary {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .summary-stats {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: bold;
          color: #1f2937;
        }

        .stat-plus, .stat-arrow {
          font-size: 1.25rem;
          color: #3b82f6;
          font-weight: bold;
        }

        .duplicates-section {
          margin-bottom: 2rem;
        }

        .duplicates-section h3 {
          font-size: 1.25rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .duplicates-section p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .duplicates-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .duplicate-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
        }

        .duplicate-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .duplicate-header h4 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0;
        }

        .similarity-score {
          font-size: 0.875rem;
          color: #3b82f6;
          background: #dbeafe;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
        }

        .criteria-comparison {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .criterion-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          padding: 1rem;
        }

        .criterion-source {
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
        }

        .criterion-card h5 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .criterion-card ul {
          margin: 0;
          padding-left: 1.25rem;
          list-style-type: disc;
        }

        .criterion-card li {
          color: #6b7280;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .action-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .action-option {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 0.375rem;
          padding: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .action-option:hover {
          border-color: #3b82f6;
        }

        .action-option.selected {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .action-option strong {
          display: block;
          color: #1f2937;
          margin-bottom: 0.25rem;
        }

        .action-option span {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .aspects-editor {
          margin-bottom: 2rem;
        }

        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .editor-header h3 {
          font-size: 1.25rem;
          color: #1f2937;
          margin: 0;
        }

        .aspects-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .aspect-editor {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
        }

        .aspect-header {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1rem;
        }

        .aspect-title-input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 1rem;
          font-weight: 600;
        }

        .aspect-title-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        .descriptions-editor {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .description-item {
          display: flex;
          gap: 0.5rem;
          align-items: flex-start;
        }

        .description-input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          resize: vertical;
          min-height: 2.5rem;
        }

        .description-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        .remove-button, .remove-desc-button {
          color: #dc2626;
        }

        .remove-desc-button {
          width: 2rem;
          height: 2rem;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .add-desc-button {
          align-self: flex-start;
        }

        .merge-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .summary-stats {
            flex-direction: column;
            gap: 0.5rem;
          }

          .stat-plus, .stat-arrow {
            transform: rotate(90deg);
          }

          .criteria-comparison {
            grid-template-columns: 1fr;
          }

          .action-options {
            grid-template-columns: 1fr;
          }

          .aspect-header {
            flex-direction: column;
            align-items: stretch;
          }

          .merge-actions {
            flex-direction: column-reverse;
          }
        }
      `}</style>
    </div>
  );
};